import React from "react";
import { Result, Button } from "antd";

/**
 * ErrorBoundary — catches uncaught JavaScript errors in child components
 * and renders a graceful fallback UI instead of crashing the entire app.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Must be a class component — React's error boundary API (getDerivedStateFromError,
 * componentDidCatch) is only available on class components as of React 18.
 *
 * Props:
 *   - fallback (optional): A custom React element to render on error.
 *                           Defaults to a user-friendly error message with a retry button.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to an external error reporting service in production
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle="An unexpected error occurred. Please try again or contact support if the issue persists."
          extra={
            <Button type="primary" onClick={this.handleReset}>
              Try Again
            </Button>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
